import { Injectable } from '@angular/core';
import { AssetStore } from './asset.store';
import { AssetQuery } from './asset.query';
import { AssetService } from '../../squidex/services/asset.service';
import { ID } from '@datorama/akita';
import { Asset, AssetFactory, ContentList } from '../../squidex';
import { of, Observable } from 'rxjs';
import { flatMap, tap, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { FilterPagination } from '../base/filter.pagination';
import { ESchemaContentStatus } from '../base/enum';
import { DatePipe } from '@angular/common';
import { AppConfigProvider } from '../../../app-config.provider';
import { AssetDirectoryFacade } from '../asset-directory-schema/asset-directory.facade';


const EMPTY = '';

@Injectable({ providedIn: 'root' })
export class AssetFacade {

  readonly datePipe: DatePipe;
  public service: AssetService;

  private _path: string = EMPTY;
  private _endPoint: string = EMPTY;

  get path(): string {
    return this._path;
  }

  get endPoint(): string {
    return this._endPoint;
  }

  private store: AssetStore;//TODO: make it static
  private query: AssetQuery;//TODO: make it static
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';


  protected constructor(private readonly assetFactory: AssetFactory,
    private readonly translateService: TranslateService,
    private readonly appConfigProvider: AppConfigProvider,
    private readonly assetDirectoryFacade: AssetDirectoryFacade,
  ) {
    this.datePipe = new DatePipe(this.getCurrentLanguage());
    this._path = appConfigProvider.getAssetsPathUrl();
    this._endPoint = appConfigProvider.getApiEndpoint();

  }


  protected initialize(): void {
    if (!this.service) {
      this.service = this.assetFactory.getAssetService();
    }
    if (!this.query) {
      this.query = this.assetFactory.getAssetQuery();
    }
    if (!this.store) {
      this.store = this.assetFactory.getAssetStore();
    }
    this._path = this.appConfigProvider.getAssetsPathUrl();
    this._endPoint = this.appConfigProvider.getApiEndpoint();
  }


  getById(id: ID): Observable<Asset> {
    this.initialize();
    if (this.query.getValue().ids.indexOf(id) > -1) {
      return of(this.query.getValue().entities[id]);
    }

    return this.service.getById(id).pipe(flatMap((asset) => {
      this.store.add(asset);
      return of(asset);
    })) as Observable<Asset>;
  }

  create(assetData: any, file?: File, directory?: string, fieldName?: string, fileName?: string, folderParentId?: string): Observable<Asset> {
    this.initialize();


    return this.service.upload(assetData)
      .pipe(
        tap(assetBack => {

          if (!fileName) {
            fileName = assetBack.fileName;
          }

          assetBack.fieldName = fieldName;
          this.service.update(assetBack.id, assetBack).toPromise().then(tagBack => {

            const payload = {
              tags: []
            }

            if (fieldName) {
              payload.tags.push("field=" + fieldName);
            }

            if (directory) {
              payload.tags.push("dir=" + directory);
              payload.tags.push("infolder");
            }

            if (assetBack.tags) {
              assetBack.tags.forEach(tag => {
                payload.tags.push(tag);
              });
            }

            if (assetBack.isImage) {
              payload.tags.push('image');
            } else {
              switch (assetBack.fileType.toLowerCase()) {
                case 'mp3':
                case 'wav':
                case 'acc':
                case 'wma':
                case 'flac':
                case 'aiff':
                  payload.tags.push('audio');
                  break;
                case 'docx':
                case 'doc':
                case 'pdf':
                case 'xlsx':
                case 'txt':
                  payload.tags.push('document');
                  break;
                default:
                  payload.tags.push('file');
              }
            }

            return this.service.updateTag(assetBack.id, payload).toPromise().then(assetTagBack => {
              const parentPayload = {
                parentId: folderParentId
              }

              return this.service.updateParent(assetBack.id, parentPayload).subscribe(assetParentBack => {
                this.store.add(assetParentBack);
                return assetParentBack;
              });
            });
          });

        })
      );
  }

  update(assetId: string, assetData: any): Observable<Asset> {
    this.initialize();

    const fname = assetData.fileName.replace(/\.[^/.]+$/, "");
    const ext = assetData.fileName.substr(assetData.fileName.lastIndexOf('.') + 1);
    assetData.slug = this.getSlug(fname) + '.' + ext;

    return this.service.update(assetId, assetData).
      pipe(
        tap(assetBack => {
          this.store.update(assetId, u => {
            return assetBack;
          });
        })
      );
  }

  updateTag(asset: any): Observable<Asset> {
    this.initialize();
    return this.service.updateTag(asset.id, asset)
      .pipe(
        tap(v => {
          this.store.add(v);
        })
      );
  }

  remove(id: ID): Observable<Asset> {
    this.initialize();
    return this.service.delete(id).pipe(
      tap(v => {
        this.store.remove(id);
      })
    );
  }

  getAssetsFromFolder(folderId?: string, filter?: any, AdvancedFilter?: any): Observable<any> {
    this.initialize();

    let contentObject =
      {
        filter:
        {
          and: []
        }
      }

    let parentId = folderId ? `&parentId=${folderId}` : '';

    if (AdvancedFilter) {
      contentObject.filter.and.push(AdvancedFilter);
    }

    if (filter) {
      let sortInformation = filter.orderBy.split(" ");

      if (sortInformation.length === 1) {
        contentObject["sort"] = [{ path: sortInformation[0] }];
      } else if (sortInformation.length === 2) {
        contentObject["sort"] = [{
          path: sortInformation[0],
          order: sortInformation[1],
        }];

        contentObject["skip"] = filter.from;
        contentObject["take"] = filter.to - filter.from;
      }
    } else {
      contentObject["take"] = 100;
      contentObject["sort"] = [{
        path: 'fileName',
        order: 'descending',
      }];

    }

    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject)) + parentId;

    return this.service.find(filterText);
  }

  fileCount(name: string, folder: string) {
    this.initialize();

    let contentObject: object = {};

    const fullName = folder + '_' + name;

    contentObject =
    {
      filter:
      {
        and: [
          {
            "path": "fileName",
            "op": "eq",
            "value": fullName,
          }
        ]
      },
    }

    contentObject["take"] = 1;

    contentObject["sort"] = [];

    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject));

    return this.service.countQuery(filterText);

  }

  folderCount(id: string): Observable<number> {
    this.initialize();

    return this.service.getCount(id)
      .pipe(map(object => {
        return object.total;
      })
    );
  }


  getFilesByName(name: string, folder: string): Observable<any> {
    this.initialize();

    let contentObject: object = {};

    const fullName = folder + '_' + name;

    contentObject =
    {
      filter:
      {
        and: [
          {
            "path": "fileName",
            "op": "eq",
            "value": fullName,
          }
        ]
      },
    }

    contentObject["take"] = 1;

    contentObject["sort"] = [];

    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject));

    return this.service.find(filterText);

  }


  getFolderCount(folder?: string): Observable<number> {
    this.initialize();

    let contentObject: object = {};

    if (folder) {
      folder = "dir=" + folder;
      contentObject =
      {
        filter:
        {
          and: [
            {
              "path": "tags",
              "op": "eq",
              "value": folder,
            }
          ]
        },
      }
    }

    contentObject["take"] = 1;

    contentObject["sort"] = [];

    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject));

    return this.service.countQuery(filterText);
  }

  getDefaultLanguage(): string {
    this.initialize();
    return this.translateService.defaultLang;
  }

  getCurrentLanguage(): string {
    // this.initialize();
    return this.translateService.currentLang;
  }

  // not tested
  getFilteredPagination(filterParms: FilterPagination,
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.Published,
    AdvancedFilter?: any
  ): Observable<Asset[]> {
    this.initialize();

    let publishedText = "";

    let contentObject =
    {
      filter:
      {
        and: []
      }
    }

    if (AdvancedFilter) {
      contentObject.filter.and.push(AdvancedFilter);
    }

    let sortInformation = filterParms.orderBy.split(" ");

    if (sortInformation.length === 1) {
      contentObject["sort"] = [{ path: sortInformation[0] }];
    } else if (sortInformation.length === 2) {
      contentObject["sort"] = [{
        path: sortInformation[0],
        order: sortInformation[1],
      }];
    }

    contentObject["skip"] = filterParms.from;
    contentObject["take"] = filterParms.to - filterParms.from;

    if (typeof filterParms.filter != "undefined" && filterParms.filter != "") {
      contentObject["fullText"] = filterParms.filter + "*";
    }

    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject));
    return this.service.find(filterText);
  }

  translate(key: string | Array<string>, interpolateParams?: Object): Observable<string | any> {
    return this.translateService.get(key, interpolateParams);
  }

  getSlug(fileName: string) {
    const slug: string = fileName.replace(/\s+/g, '-')
      .replace(/'/g, EMPTY)
      .replace(/\\/g, EMPTY)
      .replace(/\*/g, EMPTY).toLowerCase();
    return slug; //  + '-' + this.getFormattedTime();
  }

  trimFolder(fname: string): string {
    fname = fname.substring(fname.indexOf('_') + 1);
    return fname;
  }

  getFormattedTime() {
    const today = new Date();
    const y = today.getFullYear();
    const m: string = (today.getMonth() + 1).toString(); // JavaScript months are 0-based.
    const d: string = today.getDate().toString();
    const h: string = today.getHours().toString();
    const mi: string = today.getMinutes().toString();
    const s: string = today.getSeconds().toString();
    return y + m + d + "-" + h + mi + s;
  }

  generateThumbnail(pageUrl: any, imageName: string, imageId?: string): Observable<any> {
    this.initialize();
    return this.service.generateThumbnail(pageUrl, imageName, imageId)
      .pipe(
        tap(assetBack => {
          if (assetBack != null) {
            let payload = { tags: [] }
            payload.tags.push("type/png");
            payload.tags.push("dir=thumbnail");
            payload.tags.push("infolder");
            payload.tags.push('image');
            payload.tags.push('image/large');
            this.service.updateTag(assetBack.id, payload).subscribe(assetTagBack => {
              this.store.add(assetTagBack);
              if (imageId) {
                this.service.delete(imageId).subscribe(); // to do.  Done here because delete doesn't work in controller.
              }
            });
          }
        })
      );
  }
}
